import React, { useState } from 'react';
import { Formik, Form, Field, CccisdInput, CccisdToggle, CccisdFieldWrapper, CccisdSelect } from 'cccisd-formik';
import Toggle from 'cccisd-toggle';
import DatePicker from 'cccisd-datepicker';
import Table from 'cccisd-graphql-table';
import _isEqual from 'lodash/isEqual';
import PropTypes from 'prop-types';

import RequiredFieldLabel from '../RequiredFieldLabel';
import sitesQuery from '../sites.graphql';
import careCoordinatorsQuery from '../careCoordinators.graphql';
import caregiversQuery from '../caregivers.graphql';
import caregiversCCQuery from '../caregiversCC.graphql';
import { isNaN } from 'lodash';

const Fortress = window.cccisd && window.cccisd.fortress;
let currentRole = Fortress.user && Fortress.user.acting.role.handle;

const YouthForm = props => {
    const { onSubmit, initialValues } = props;

    const [age, setAge] = useState(null);
    const [birthdatePickerValue, setBirthdatePickerValue] = useState(
        initialValues.dateOfBirth ? initialValues.dateOfBirth : null
    );
    const [receiveMessagesValue, setReceiveMessagesValue] = useState(initialValues.receiveMessages);

    const onHandleSubmit = async values => {
        values.site = values.site[0];
        values.role = 'youth';
        values.receiveMessages = receiveMessagesValue;
        values.dateOfBirth = birthdatePickerValue;

        await onSubmit(values);
    };

    // TODO need WrapStat’s eventHook

    const validate = values => {
        const errors = {};

        if (!values.wrapId) {
            errors.wrapId = 'Wrap ID must be unique to each caregiver and is required.';
        }
        if (!values.phoneNumber && receiveMessagesValue) {
            errors.phoneNumber = 'Phone number is required.';
        }
        if (values.phoneNumber && values.phoneNumber) {
            if (!/^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/.test(values.phoneNumber)) {
                errors.phoneNumber = 'Must be a valid phone number.';
            }
        }
        if (values.site.length < 1) {
            errors.site = 'Site is required.';
        }
        if (values.site.length > 1) {
            errors.site = 'You may select only 1 Site.';
        }
        if (values.careCoordinator < 1) {
            errors.careCoordinator = 'Care Coordinator is required.';
        }
        if (values.careCoordinator.length > 1) {
            errors.careCoordinator = 'You may select only 1 Care Coordinator.';
        }
        if ((!values.youngAdult || values.youngAdult === null) && values.caregiver < 1) {
            errors.caregiver = 'Caregiver is required if Youth is under 18 years of age.';
        }
        if (!birthdatePickerValue) {
            errors.dateOfBirth = 'Date of birth is required.';
        }

        return errors;
    };

    const calculateAge = dateOfBirth => {
        const today = new Date();
        const dob = new Date(dateOfBirth);

        let res = today.getFullYear() - dob.getFullYear();
        const monthDiff = today.getMonth() - dob.getMonth();
        const dayDiff = today.getDate() - dob.getDate();

        // Adjust if birthday hasn't occurred yet this year
        if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
            res--;
        }

        return res;
    };

    const siteColumns = [
        {
            name: 'group.groupId',
            label: 'ID',
            hideInTable: true,
        },
        {
            name: 'group.label',
            label: 'Site',
            sort: true,
            filter: true,
        },
        {
            name: 'ancestorGroups.agency.group.label',
            label: 'Agency',
            sort: true,
            filter: true,
        },
        {
            name: 'ancestorGroups.organization.group.label',
            label: 'Organization',
            sort: true,
            filter: true,
        },
        {
            name: 'ancestorGroups.collaborator.group.label',
            label: 'Collaborator',
            sort: true,
            filter: true,
        },
    ];

    const careCoordinatorColumns = [
        {
            name: 'pawn.pawnId',
            label: 'ID',
            hideInTable: true,
        },
        {
            name: 'user.fullName',
            label: 'Care Coordinator',
            sort: true,
            filter: true,
        },
        {
            name: 'parentRoles.supervisor.user.fullNameWithUsername',
            label: 'Supervisor',
            sort: true,
            filter: true,
        },
        {
            name: 'parentGroup.site.childRoles.siteAdmin.user.fullNameWithUsername',
            label: 'Site Admin',
            sort: true,
            filter: true,
        },
    ];

    const caregiverColumns = [
        {
            name: 'pawn.pawnId',
            label: 'ID',
            hideInTable: true,
        },
        {
            name: 'fields.firstName',
            label: 'First Name',
            sort: true,
            filter: true,
        },
        {
            name: 'fields.lastName',
            label: 'Last Name',
            sort: true,
            filter: true,
        },
        {
            name: 'fields.phoneNumber',
            label: 'Phone',
            sort: true,
            filter: true,
        },
    ];

    return (
        <Formik onSubmit={e => onHandleSubmit(e)} validate={e => validate(e)} initialValues={initialValues}>
            {({ handleSubmit, values, setFieldValue, isSubmitting }) => {
                return (
                    <Form>
                        <Field
                            name="wrapId"
                            component={CccisdInput}
                            label={<RequiredFieldLabel isRequired>Wrap ID (must be unique):</RequiredFieldLabel>}
                        />
                        <Field
                            name="youthStatus"
                            component={CccisdSelect}
                            label="Youth Status:"
                            options={[
                                { value: 'Active', label: 'Active' },
                                { value: 'Discharged', label: 'Discharged' },
                            ]}
                        />
                        <Field name="firstName" component={CccisdInput} label="First Name:" />
                        <Field name="lastName" component={CccisdInput} label="Last Name:" />

                        <Field name="dateOfBirth">
                            {({ field, form }) => {
                                return (
                                    <CccisdFieldWrapper
                                        label={
                                            <div>
                                                <RequiredFieldLabel isRequired>Date of Birth:</RequiredFieldLabel>
                                            </div>
                                        }
                                        form={form}
                                        field={field}
                                    >
                                        <div>
                                            <DatePicker
                                                selected={
                                                    birthdatePickerValue
                                                        ? birthdatePickerValue
                                                        : initialValues.dateOfBirth
                                                }
                                                onChange={val => {
                                                    const today = new Date();
                                                    if (
                                                        isNaN(val.getFullYear()) ||
                                                        isNaN(val.getMonth()) ||
                                                        isNaN(val.getDate()) ||
                                                        val.getFullYear() > today.getFullYear()
                                                    ) {
                                                        return;
                                                    }

                                                    const year = val.getFullYear();
                                                    const month = (val.getMonth() + 1).toString().padStart(2, '0');
                                                    const day = val.getDate().toString().padStart(2, '0');
                                                    const res = `${year}-${month}-${day}`;

                                                    setBirthdatePickerValue(res);
                                                    const calcedAge = calculateAge(val);

                                                    setAge(calcedAge);

                                                    if (calcedAge >= 18) {
                                                        setFieldValue('youngAdult', true);
                                                        setReceiveMessagesValue(true);
                                                    } else {
                                                        setFieldValue('youngAdult', false);

                                                        if (calcedAge < 14) {
                                                            setReceiveMessagesValue(false);
                                                        } else {
                                                            setReceiveMessagesValue(true);
                                                        }
                                                    }
                                                }}
                                                placeholderText="MM/DD/YYYY"
                                                dateFormat="MM/dd/yyyy"
                                            />
                                        </div>
                                    </CccisdFieldWrapper>
                                );
                            }}
                        </Field>
                        <Field name="youngAdult" component={CccisdToggle} label="This Youth is 18 or older" />
                        <Toggle
                            label={
                                <div>
                                    <div>This Youth will receive messages</div>
                                </div>
                            }
                            onChange={val => {
                                setReceiveMessagesValue(!receiveMessagesValue);
                            }}
                            value={receiveMessagesValue}
                            name="receiveMessages"
                        />
                        {age !== null && age !== undefined && age < 14 && !receiveMessagesValue && (
                            <div
                                style={{
                                    position: 'relative',
                                    color: 'orange',
                                }}
                            >
                                Youths under 14 do not receive messages by default.
                            </div>
                        )}
                        <br />
                        <Field
                            name="phoneNumber"
                            component={CccisdInput}
                            label={
                                receiveMessagesValue ? (
                                    <RequiredFieldLabel isRequired>Phone Number:</RequiredFieldLabel>
                                ) : (
                                    'Phone Number:'
                                )
                            }
                        />
                        <Field
                            name="language"
                            component={CccisdSelect}
                            label="Language:"
                            options={[
                                { value: '', label: '-- Choose language --' },
                                { value: 'en', label: 'English' },
                                { value: 'es', label: 'Spanish' },
                            ]}
                        />
                        {currentRole !== 'careCoordinator' && (
                            <Field name="site" enableReinitialize>
                                {({ field, form }) => {
                                    return (
                                        <CccisdFieldWrapper
                                            field={field}
                                            form={form}
                                            label={<RequiredFieldLabel isRequired>Site:</RequiredFieldLabel>}
                                        >
                                            <Table
                                                query={sitesQuery}
                                                rowKey="group.groupId"
                                                columns={siteColumns}
                                                csvFilename="Sites.csv"
                                                showRowActions={false}
                                                rowSelectedCallback={selectedRows => {
                                                    if (!_isEqual(selectedRows, field.value)) {
                                                        setFieldValue('site', selectedRows);
                                                        setFieldValue('careCoordinator', []);
                                                        setFieldValue('caregiver', []);
                                                    }
                                                }}
                                                selectedRows={initialValues.site}
                                                maxSelectableRows={1}
                                                rowActions={[
                                                    {
                                                        name: 'selectSite',
                                                        title: 'Add to Site',
                                                        action: () => {},
                                                    },
                                                ]}
                                            />
                                        </CccisdFieldWrapper>
                                    );
                                }}
                            </Field>
                        )}

                        {values.site &&
                            (Array.isArray(values.site) ? values.site.length === 1 : values.site) &&
                            currentRole !== 'careCoordinator' && (
                                <>
                                    <Field name="careCoordinator" enableReinitialize>
                                        {({ field, form }) => {
                                            return (
                                                <CccisdFieldWrapper
                                                    field={field}
                                                    form={form}
                                                    label={
                                                        <RequiredFieldLabel isRequired>
                                                            Care Coordinator:
                                                        </RequiredFieldLabel>
                                                    }
                                                >
                                                    <Table
                                                        query={careCoordinatorsQuery}
                                                        rowKey="pawn.pawnId"
                                                        columns={careCoordinatorColumns}
                                                        csvFilename="Care Coordinators.csv"
                                                        showRowActions={false}
                                                        rowSelectedCallback={selectedRows => {
                                                            if (!_isEqual(selectedRows, field.value)) {
                                                                setFieldValue('careCoordinator', selectedRows);
                                                            }
                                                        }}
                                                        selectedRows={
                                                            // if the site is edited, careCoordinator should be []
                                                            !_isEqual(form.values.site, initialValues.site)
                                                                ? field.value
                                                                : initialValues.careCoordinator
                                                        }
                                                        graphqlVariables={{
                                                            siteId: values.site[0],
                                                        }}
                                                        maxSelectableRows={1}
                                                        rowActions={[
                                                            {
                                                                name: 'selectCareCoordinator',
                                                                title: 'Link to Care Coordinator',
                                                                action: () => {},
                                                            },
                                                        ]}
                                                    />
                                                </CccisdFieldWrapper>
                                            );
                                        }}
                                    </Field>
                                </>
                            )}
                        {values.site && (Array.isArray(values.site) ? values.site.length === 1 : values.site) && (
                            <>
                                <Field name="caregiver" enableReinitialize>
                                    {({ field, form }) => {
                                        return (
                                            <CccisdFieldWrapper
                                                field={field}
                                                form={form}
                                                label={
                                                    !values.youngAdult || values.youngAdult === null ? (
                                                        <RequiredFieldLabel isRequired>Caregiver:</RequiredFieldLabel>
                                                    ) : (
                                                        <div>Caregiver:</div>
                                                    )
                                                }
                                            >
                                                <Table
                                                    query={
                                                        currentRole !== 'careCoordinator'
                                                            ? caregiversQuery
                                                            : caregiversCCQuery
                                                    }
                                                    rowKey="pawn.pawnId"
                                                    columns={caregiverColumns}
                                                    csvFilename="Caregivers.csv"
                                                    showRowActions={false}
                                                    rowSelectedCallback={selectedRows => {
                                                        if (!_isEqual(selectedRows, field.value)) {
                                                            setFieldValue('caregiver', selectedRows);
                                                        }
                                                    }}
                                                    selectedRows={
                                                        // if the site is edited, caregiver should be []
                                                        !_isEqual(form.values.site, initialValues.site)
                                                            ? field.value
                                                            : initialValues.caregiver.map(caregiver => {
                                                                  return caregiver.pawn.pawnId;
                                                              })
                                                    }
                                                    graphqlVariables={{
                                                        siteId: values.site[0],
                                                    }}
                                                    rowActions={[
                                                        {
                                                            name: 'selectCaregiver',
                                                            title: 'Link to Caregiver',
                                                            action: () => {},
                                                        },
                                                    ]}
                                                />
                                            </CccisdFieldWrapper>
                                        );
                                    }}
                                </Field>
                            </>
                        )}
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                marginTop: '1em',
                            }}
                        >
                            <button type="button" className="btn btn-primary" onClick={() => handleSubmit()}>
                                Submit
                            </button>
                        </div>
                    </Form>
                );
            }}
        </Formik>
    );
};

YouthForm.propTypes = {
    onSubmit: PropTypes.func,
    initialValues: PropTypes.object,
};

export default YouthForm;
